import { useEffect, useState } from "react";
import { PubTournament } from "../../../../../../api/types";
import { getRunnerCompetitionTournament } from "../../../../../../api/runner-competition-tournament";
import {
  getRunnerCompetitionMainList,
  RunnerCompetitionDto,
} from "../../../../../../api/runner-competition";
import { calculateTimes } from "../../../../../../utils/timer";
import { useRecoilValue } from "recoil";
import { currentTimeState } from "../../../../../../recoil/app";
import { userState } from "../../../../../../recoil/auth";

export const useRabbitFestivalMain = () => {
  const currentTime = useRecoilValue(currentTimeState);
  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState<PubTournament[]>([]);
  const [tournamentList, setTournamentList] = useState<PubTournament[]>([]);
  const [competitions, setCompetitions] = useState<RunnerCompetitionDto[]>([]);
  const [offset, setOffset] = useState(0);
  const user = useRecoilValue(userState);

  const setTournamentData = () => {
    if (tournamentList.length === 0) return;
    const list: PubTournament[] = [];
    for (var tournament of tournamentList) {
      if (tournament.timerDto) {
        const time = calculateTimes(
          tournament.timerDto,
          tournament.structure,
          true,
          offset
        );
        // 타이머 존재하고 마지막 레벨 한 번 더 반복되지 않은 상태만 타이머 정보 추가
        if (time && time.overStack < 1) {
          tournament.timeInfo = time;
        }
        list.push(tournament);
      } else {
        list.push(tournament);
      }
    }
    const sorted = list.sort((a, b) => {
      const getPriority = (t: PubTournament) => {
        if (t?.timeInfo?.elapsedTimeMs > 0 && !t.isEnd && t.timeInfo) return 1; // 진행중
        if (t.timeInfo && t?.timeInfo?.elapsedTimeMs < 0 && t.reservation)
          return 2; // 예약 존재 (미 종료)
        if (t.timeInfo && t?.timeInfo?.elapsedTimeMs < 0 && !t.reservation)
          return 3; // 예약 미존재 (미 종료)
        if (t.isEnd || !t.timeInfo) return 4; // 종료
        return 5;
      };

      const pa = getPriority(a);
      const pb = getPriority(b);

      const totalA = a.moneyInChip.reduce(
        (sum, curr) => sum + (curr?.point || 0),
        0
      );
      const totalB = b.moneyInChip.reduce(
        (sum, curr) => sum + (curr?.point || 0),
        0
      );

      // 우선순위 비교 (작은 숫자 먼저)
      if (pa !== pb) return pa - pb;

      // 날짜 비교 함수 비교
      const getDateOnly = (d: string) => {
        const date = new Date(d);
        return new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        ).getTime();
      };

      // 예약 존재 토너먼트 비교
      if (pa === 2 && pb === 2) {
        // 날짜가 다르면 가까운 날짜 순 (과거 → 미래)
        const dateDiff = getDateOnly(a.startDate) - getDateOnly(b.startDate);
        if (dateDiff !== 0) return dateDiff;

        // 시작일이 같을 경우, 유저 기준 추가 조건
        if (user) {
          const seedA = (a?.buyIn || 0) / 10000;
          const seedB = (b?.buyIn || 0) / 10000;
          const userSeed = user?.seed || 0;

          // 두 토너먼트 모두 예약 불가할 경우
          // → 총 프라이즈(머니인칩) 높은 순
          if (seedA > userSeed && seedB > userSeed) return totalB - totalA;

          const canReserveA = seedA <= userSeed ? 1 : 0;
          const canReserveB = seedB <= userSeed ? 1 : 0;

          // 예약 가능 여부 우선
          if (canReserveA !== canReserveB) return canReserveB - canReserveA;

          // 둘 다 예약 가능일 경우 → 시드 높은 순
          return seedB - seedA;
        }

        // 유저 정보가 없을 경우 → 총 프라이즈 높은 순
        return totalB - totalA;
      }

      // 그 외 → 총 프라이즈 높은 순
      if (totalB === totalA) {
        // 총 프라이즈 동일 시 날짜 비교(최신순)
        const dateDiff = getDateOnly(b.startDate) - getDateOnly(a.startDate);
        if (dateDiff !== 0) return dateDiff;
      }
      return totalB - totalA;
    });

    // 예약 존재하는 토너만 추출 (종료되지 않은 것)
    const reservations = sorted.filter(
      (t) =>
        !t.isEnd &&
        t.timeInfo &&
        (t.reservation || t?.timeInfo?.elapsedTimeMs > 0)
    );

    setTournaments(
      reservations.length ? reservations : sorted.length ? [sorted[0]] : []
    );
  };

  const getTournamentData = () => {
    if (!tournaments.length) return;

    let list: PubTournament[] = [];
    tournaments.forEach((item) => {
      if (item?.timerDto) {
        const time = calculateTimes(
          item.timerDto,
          item.structure,
          true,
          offset
        );
        if (time && time.overStack < 1) {
          item.timeInfo = time;
          list.push(item);
        } else {
          list.push(item);
        }
      } else {
        list.push(item);
      }
    });
    setTournaments(list);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [competitionsRes, tournamentsRes] = await Promise.all([
          getRunnerCompetitionMainList(),
          getRunnerCompetitionTournament(),
        ]);

        setCompetitions(competitionsRes);
        setTournamentList(tournamentsRes);

        setTimeout(() => {
          setLoading(false);
        }, 50);
      } catch (err) {
        console.error("데이터 불러오기 실패:", err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    setTournamentData();
  }, [tournamentList]);

  useEffect(() => {
    if (!tournaments.length) return;
    if (offset === 0 && tournaments[0].timerDto?.now) {
      const serverNow = new Date(tournaments[0].timerDto.now).getTime();
      const clientNow = Date.now();
      setOffset(serverNow - clientNow);
    }
  }, [tournaments]);

  useEffect(() => {
    getTournamentData();
  }, [currentTime, tournamentList, offset]);

  return {
    tournaments,
    competitions,
    loading,
  };
};
